import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { DalState, GeogebraService, ResultActions } from '@campus/dal';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GeoGebraWidgetConfigInterface } from '../../interfaces/geogebra-widget.service.interface';
import { GeogebraWidgetService } from '../../services';

@Component({
    selector: 'campus-geogebra-assessement',
    templateUrl: './assessement.component.html',
    styleUrls: ['./assessement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: '[ relative index-overlay ] [ h-full w-full m-0 ] [ top-0 left-0 ] [ surface ]' },
    providers: [GeogebraWidgetService],
    standalone: false
})
export class AssessementComponent implements OnInit, OnDestroy {
  private dialogData: any = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef);

  private store = inject<Store<DalState>>(Store);
  private geogebraService = inject(GeogebraService);
  private geogebraWidgetService = inject(GeogebraWidgetService);

  private widgetConfig: GeoGebraWidgetConfigInterface = {
    elementId: 'geogebra_assess',
    initialData: this.dialogData.data,
    isComplete: this.dialogData.context.isComplete,
    shouldUpdateScore: this.dialogData.context.saveToApi,
    appName: 'classic',
    scaleContainerClass: 'widget-container',
    width: 1200,
    height: 600,
    // autoHeight: true,
    // allowUpscale: true,
    showMenuBar: false,
    showAlgebraInput: false,
    showToolBar: true,
    showToolBarHelp: false,
    showResetIcon: true,
    enableLabelDrags: false,
    enableShiftDragZoom: true,
    enableRightClick: false,
    errorDialogsActive: true,
    useBrowserForJS: false,
    preventFocus: false,
    // showLogging: true,
    allowStyleBar: false,
  };

  public disabled$: Observable<{ value: boolean }>;
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.geogebraWidgetService.init(this.widgetConfig);

    this.subscriptions.add(
      this.geogebraWidgetService.score$.subscribe(({ score, interval, isComplete }) => {
        this.updateScore(score, interval, isComplete);
      })
    );

    this.disabled$ = this.getDisabled$();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateScore(score: number, time: number, isComplete: boolean): void {
    const data = this.geogebraWidgetService.getData();

    const { context } = this.dialogData;
    const { personId, taskId, eduContentId, resultId, unlockedFreePracticeId, result } = context;

    this.upsertGeogebraSession({
      id: resultId || result?.id,
      score,
      time,
      data,
      taskId,
      userId: personId,
      eduContentId,
      isComplete,
      unlockedFreePracticeId,
    });
  }

  public onCloseClicked() {
    this.dialogRef.close();
  }

  public completeSession() {
    this.geogebraWidgetService.completeSession();
  }

  private upsertGeogebraSession(ggbSession): void {
    this.geogebraService.upsertGeogebraSession(ggbSession).subscribe((result) => {
      this.store.dispatch(new ResultActions.UpsertResult({ result }));
    });
  }

  private getDisabled$(): Observable<{ value: boolean }> {
    if (!this.dialogData.context.saveToApi) return of({ value: true });
    return this.geogebraWidgetService.isComplete$.pipe(map((isComplete) => ({ value: isComplete })));
  }
}
